import React, { useCallback, useEffect, useState } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from "recharts";
import { ShieldCheck, FileDown, Save, CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const SCORE_LABEL = { 0: "Not observed", 1: "Partially", 2: "Fully observed" };

function downloadBlob(res, name) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function ScorePicker({ value, onChange, idBase }) {
  return (
    <div className="flex gap-1">
      {[0, 1, 2].map((sc) => (
        <button key={sc} data-testid={`${idBase}-sc-${sc}`} onClick={() => onChange(sc)} title={SCORE_LABEL[sc]}
          className={`w-7 h-7 rounded text-[12px] font-semibold border transition-colors ${value === sc
            ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]"
            : "border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}>{sc}</button>
      ))}
    </div>
  );
}

export default function EvidenceMaturity({ clientId }) {
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/evidence-maturity`);
      setData(data); setResponses(data.responses || {});
    } catch { toast.error("Could not load maturity model"); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const setResp = (id, sc) => setResponses((r) => ({ ...r, [id]: sc }));

  const save = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.put(`/admin/enterprise360/clients/${clientId}/evidence-maturity`, { responses });
      setData((d) => ({ ...d, assessment: res.assessment, responses: res.responses, proof_gap_trend: res.proof_gap_trend }));
      toast.success("Self-assessment saved");
    } catch { toast.error("Could not save"); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try { downloadBlob(await api.get(`/admin/enterprise360/clients/${clientId}/evidence-maturity.pdf`, { responseType: "blob" }), "Evidence_Graph_Maturity.pdf"); }
    catch { toast.error("Could not download report"); }
  };
  const blankPdf = async () => {
    try { downloadBlob(await api.get(`/admin/enterprise360/evidence-maturity/blank.pdf`, { responseType: "blob" }), "Evidence_Graph_Maturity_workshop.pdf"); }
    catch { toast.error("Could not download workshop sheet"); }
  };

  if (!data) return <div className="text-sm text-zinc-400 py-4">Loading maturity model…</div>;
  const a = data.assessment;
  const bench = a.sector_benchmarks?.["UK bank"] || {};
  const radarData = a.pillars.map((p) => ({
    pillar: p.name, Self: p.self_total ?? 0, Evidenced: p.evidenced_score ?? 0, "UK bank": bench[p.key] ?? 0,
  }));

  return (
    <div data-testid="evidence-maturity">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-4">
        <ShieldCheck size={12} className="inline mr-1" /> Evidence Graph Maturity (EGMM) — five pillars, each scored on five
        statements 0/1/2 (not · partially · fully observed), summing to 0–10. <b>Self</b> is the client's evidenced rating;
        <b> Evidenced</b> is what this engagement's live machinery can prove. Bands: Absent 0-2 · Ad-hoc 2-4 · Defined 4-6 · Managed 6-8 · Continuous 8-10.
      </p>

      {a.proof_gap && (() => {
        const pg = a.proof_gap;
        const tone = {
          red: "bg-rose-50 border-rose-200 border-l-rose-500 text-rose-800",
          amber: "bg-amber-50 border-amber-200 border-l-amber-500 text-amber-800",
          green: "bg-emerald-50 border-emerald-200 border-l-emerald-500 text-emerald-800",
        }[pg.tone] || "bg-amber-50 border-amber-200 border-l-amber-500 text-amber-800";
        return (
          <div data-testid="maturity-proof-gap" className={`rounded-lg border border-l-4 px-4 py-3 mb-5 ${tone}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em]">
              <AlertTriangle size={12} /> Claimed vs provable — the proof gap
            </div>
            <p data-testid="maturity-proof-gap-headline" className="text-[14px] font-medium text-zinc-900 mt-1">{pg.headline}</p>
            {(pg.under_proven || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {pg.under_proven.slice(0, 4).map((u) => (
                  <span key={u.name} data-testid={`maturity-proof-gap-pillar-${u.name}`}
                    className="text-[11px] rounded-full bg-white/70 border border-black/10 px-2 py-0.5">
                    {u.name}: claims {u.self} · proves {u.evidenced} <b>(−{u.delta})</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {data.proof_gap_trend?.count >= 2 && (() => {
        const t = data.proof_gap_trend;
        const gaps = t.series.map((s) => s.gap).filter((g) => g != null);
        const max = Math.max(...gaps.map(Math.abs), 1);
        return (
          <div data-testid="maturity-proof-gap-trend" className="rounded-lg border border-zinc-200 bg-white px-4 py-3 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Proof-gap trend · {t.count} readings</div>
              {t.narrowed != null && (
                <span data-testid="maturity-proof-gap-narrowed" className={`text-[11px] font-semibold ${t.narrowed > 0 ? "text-emerald-700" : t.narrowed < 0 ? "text-rose-700" : "text-zinc-500"}`}>
                  {t.narrowed > 0 ? `▼ Gap narrowed ${t.narrowed} pts` : t.narrowed < 0 ? `▲ Gap widened ${Math.abs(t.narrowed)} pts` : "No change"} since first reading
                </span>
              )}
            </div>
            <div className="flex items-end gap-1.5 mt-2 h-12">
              {t.series.map((s, i) => (
                <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${(s.at || "").slice(0, 10)} · gap ${s.gap}`}>
                  <div className="w-full max-w-[24px] rounded-t bg-[var(--e360-primary)]/70" style={{ height: `${Math.max(4, (Math.abs(s.gap) / max) * 44)}px` }} />
                  <span className="text-[9px] text-zinc-400 mt-0.5">{s.gap}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">Each save records a reading — a narrowing gap is proof the engagement is turning asserted maturity into provable maturity.</p>
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-4 mb-5">
        <div data-testid="maturity-radar" className="border border-zinc-200 rounded-lg p-3">
          <ResponsiveContainer width="100%" height={290}>
            <RadarChart data={radarData} outerRadius="70%">
              <PolarGrid />
              <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 10]} tickCount={6} tick={{ fontSize: 9 }} />
              <Radar name="Evidenced" dataKey="Evidenced" stroke="#7737F6" fill="#7737F6" fillOpacity={0.3} />
              <Radar name="Self" dataKey="Self" stroke="#0d9488" fill="#0d9488" fillOpacity={0.12} />
              <Radar name="UK bank" dataKey="UK bank" stroke="#a1a1aa" fill="#a1a1aa" fillOpacity={0} strokeDasharray="4 3" />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-[var(--e360-primary)]/40 bg-[var(--e360-primary)]/[0.04] rounded-lg p-4 flex flex-col justify-center">
          <div className="flex gap-6 mb-3">
            <div><div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Self aggregate</div>
              <div data-testid="maturity-self-overall" className="text-3xl font-bold text-teal-700">{a.self_aggregate ?? "—"}<span className="text-lg text-zinc-400">/10</span></div>
              <div className="text-[11px] text-zinc-500">{a.self_aggregate_band || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400">Evidenced</div>
              <div data-testid="maturity-evidenced-overall" className="text-3xl font-bold text-[var(--e360-primary)]">{a.evidenced_aggregate}<span className="text-lg text-zinc-400">/10</span></div>
              <div className="text-[11px] text-zinc-500">{a.evidenced_aggregate_band}</div></div>
          </div>
          <p data-testid="maturity-verdict" className="text-[13px] text-zinc-700 leading-relaxed mb-2">{a.verdict}</p>
          <p className="text-[12px] text-zinc-500"><b>Profile:</b> {a.profile_pattern}</p>
          <div data-testid="maturity-erio" className="mt-2 text-[12px] text-zinc-600 bg-white/60 border border-zinc-200 rounded px-2.5 py-1.5">
            <b>ERIO readiness:</b> {a.erio_readiness?.recommendation}
            {a.erio_readiness?.blocked && <span className="flex items-center gap-1 text-amber-700 mt-1"><AlertTriangle size={12} /> {a.erio_readiness.blocked}</span>}
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <button data-testid="maturity-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40"><Save size={13} /> Save</button>
            <button data-testid="maturity-pdf" onClick={pdf} className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded hover:bg-[var(--e360-primary)]/5"><FileDown size={13} /> Report</button>
            <button data-testid="maturity-blank-pdf" onClick={blankPdf} className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-zinc-600 border border-zinc-300 px-3 py-1.5 rounded hover:bg-zinc-50"><FileDown size={13} /> Workshop sheet</button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {data.catalogue.pillars.map((pillar) => {
          const ass = a.pillars.find((x) => x.key === pillar.key) || {};
          return (
            <div key={pillar.key} data-testid={`maturity-pillar-${pillar.key}`} className="border border-zinc-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                <span className="text-[13px] font-semibold text-zinc-800">{pillar.name}</span>
                <span className="text-[11px] text-zinc-500">
                  Self <b className="text-teal-700">{ass.self_total ?? "—"}/10</b> · Evidenced <b className="text-[var(--e360-primary)]">{ass.evidenced_score}/10 {ass.evidenced_band}</b>
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 mb-3">{pillar.definition} — {pillar.question}</p>
              <div className="grid lg:grid-cols-2 gap-x-6 gap-y-2.5">
                <div className="space-y-2.5">
                  {pillar.statements.map((st) => (
                    <div key={st.id} className="flex items-start justify-between gap-3">
                      <span className="text-[12px] text-zinc-600 flex-1"><span className="text-zinc-400 mr-1">L{st.level}</span>{st.text}</span>
                      <ScorePicker value={responses[st.id]} onChange={(sc) => setResp(st.id, sc)} idBase={`maturity-${st.id}`} />
                    </div>
                  ))}
                </div>
                <div className="bg-zinc-50 border border-zinc-100 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 mb-1.5">What the platform observed</div>
                  <ul className="space-y-1">
                    {(ass.reasons || []).map(([mark, reason], i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[11px] text-zinc-600">
                        {mark === "+" ? <CheckCircle2 size={12} className="text-emerald-600 mt-0.5 shrink-0" /> : <Circle size={12} className="text-zinc-300 mt-0.5 shrink-0" />}
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
